/* eslint-disable react-native/no-inline-styles */
/**
 * @description - Tạo bảng dữ liệu và phân trang
 * @copyright PVcomBank 2021
 */
import { Constant } from "app/shared/constant/constant";
import { spacing } from "app/shared/styles/spacing";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import { DataTable, useTheme, Paragraph, IconButton } from "react-native-paper";
import { Block } from "../blocks/block";

export type ActionDataTable = "delete" | "show" | "";

const numberOfItemsPerPageList = [5, 10, 15];
interface ItemHeader {
  name: string;
  attr: string;
  isCustomDataColumn?: boolean;
  type?: string;
  notCenter?: boolean;
  keyCardAtmType?: string;
  action?: ActionDataTable;
}

interface DataTableView {
  header: ItemHeader[];
  dataTable: Array<any>;
  page?: number;
  perPage?: number;
  style?: ViewStyle;
  headerStyle?: StyleProp<ViewStyle>;
  labelHeaderStyle?: StyleProp<TextStyle>;
  widthCell?: number;
  isPaginationView?: boolean;
  onPressRow?: (item: any) => void;
  onPressChildren?: (item: any, type?: ActionDataTable) => void;
}

const getDataTextStyle = (object: any, header: ItemHeader, colors: ReactNativePaper.ThemeColors) => {
  /**
   * Xử lí màu với những trường hợp đặc biệt
   */
  if (!(header.isCustomDataColumn && header.type)) {
    return {};
  }
  const _value: string = header.attr
    .replace(/\[/g, ".")
    .replace(/\]/g, "")
    .split(".")
    .reduce((o: any, k: any) => (o || {})[k], object);
  let _transAtmType = "";
  if (header.keyCardAtmType) {
    _transAtmType = header.keyCardAtmType
      .replace(/\[/g, ".")
      .replace(/\]/g, "")
      .split(".")
      .reduce((o: any, k: any) => (o || {})[k], object);
  }

  switch (header.type) {
    case Constant.DEBIT:
      if (parseFloat(_value) !== 0) {
        return { color: colors.error };
      }
      break;
    case Constant.CREDIT:
      if (parseFloat(_value) !== 0) {
        return { color: colors.success };
      }
      break;
    case Constant.FEE:
      return { color: colors.error };
    case Constant.CARD.CARD_HISTORY_TRANS_TYPE:
      if (_transAtmType === Constant.CARD.CARD_TRANS_TYPE.CR && parseFloat(_value) !== 0) return { color: colors.success };
      else if (_transAtmType === Constant.CARD.CARD_TRANS_TYPE.DB && parseFloat(_value) !== 0) return { color: colors.error };
      else return {};
    default:
      return {};
  }
};

const DataTableView = ({
  header = [],
  dataTable = [],
  perPage = 5,
  widthCell = undefined,
  page = 0,
  isPaginationView = true,
  headerStyle = {},
  labelHeaderStyle = {},
  onPressRow = () => {},
  onPressChildren = () => {},
}: DataTableView) => {
  const [state, setState] = useState({
    dataTable,
    page,
    perPage,
    numberOfPages: Math.ceil(dataTable.length / perPage),
  });

  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        tableHeader: { backgroundColor: colors.primary, borderBottomWidth: 0 },
        tableRow: { borderBottomWidth: 0 },
        cellStyles: {
          flex: !widthCell ? 1 : undefined,
          minWidth: widthCell ? widthCell : undefined,
          maxWidth: widthCell ? widthCell : undefined,
          alignSelf: "center",
          textAlignVertical: "center",
          paddingHorizontal: 4,
        },
        firstIndexCellStyles: {
          maxWidth: 40,
          minWidth: 40,
          alignSelf: "center",
          textAlignVertical: "center",
          paddingHorizontal: 4,
          alignItems: "center",
        },
        rowStyles: {
          backgroundColor: colors.surface,
          borderWidth: 0,
          borderBottomWidth: 0,
        },
        evenRowStyles: {
          backgroundColor: colors.select,
          borderWidth: 0,
          borderBottomWidth: 0,
        },
        cellContent: {
          ...fonts.medium,
          fontSize: 12,
        },
        titleContentStyle: {
          ...fonts.semibold,
          color: colors.accent,
          fontSize: 12,
          textAlign: "center",
        },
      }),
    [colors, fonts, widthCell],
  );

  const getValue = (object: any, headerItem: ItemHeader): string => {
    if (!(headerItem.isCustomDataColumn && headerItem.type)) {
      const _rowValue = headerItem.attr
        .replace(/\[/g, ".")
        .replace(/\]/g, "")
        .split(".")
        .reduce((o: any, k: any) => (o || {})[k], object);
      return _rowValue || "";
    }
    const _value: string = headerItem.attr
      .replace(/\[/g, ".")
      .replace(/\]/g, "")
      .split(".")
      .reduce((o: any, k: any) => (o || {})[k], object);
    let _transAtmType = "";
    if (headerItem.keyCardAtmType) {
      _transAtmType = headerItem.keyCardAtmType
        .replace(/\[/g, ".")
        .replace(/\]/g, "")
        .split(".")
        .reduce((o: any, k: any) => (o || {})[k], object);
    }
    switch (headerItem.type) {
      case Constant.DEBIT:
        if (_value !== "" && _value !== "0") {
          return "- " + _value + " " + t("COMMON_VND");
        }
        break;
      case Constant.CREDIT:
        if (_value !== "" && _value !== "0") {
          return "+ " + _value + " " + t("COMMON_VND");
        }
        break;
      case Constant.FEE:
        if (_value !== "" && _value !== "0") {
          return "- " + _value + " " + t("COMMON_VND");
        }
        return "0" + " " + t("COMMON_VND");
      case Constant.AMOUNT:
        if (_value !== "" && _value !== "0") {
          return _value + " " + t("COMMON_VND");
        }
        return "0" + " " + t("COMMON_VND");
      case Constant.CARD.CARD_HISTORY_TRANS_TYPE:
        if (_transAtmType === Constant.CARD.CARD_TRANS_TYPE.CR && _value !== "" && _value !== "0") {
          return "+ " + _value + " " + t("COMMON_VND");
        } else if (_transAtmType === Constant.CARD.CARD_TRANS_TYPE.DB && _value !== "" && _value !== "0") {
          return "- " + _value + " " + t("COMMON_VND");
        }
        return "0" + " " + t("COMMON_VND");
      default:
        return "";
    }
    return "";
  };

  const onPressRowTable = React.useCallback(
    (item: any) => {
      onPressRow(item);
    },
    [onPressRow],
  );

  // Thay đổi bảng nếu đầu vào thay đổi
  React.useEffect(() => {
    if (JSON.stringify(dataTable) !== JSON.stringify(state.dataTable)) {
      setState({ ...state, dataTable, numberOfPages: Math.ceil(dataTable.length / state.perPage), page: 0 });
    }
  }, [dataTable, state]);

  const tableView = (
    <>
      <DataTable.Header style={headerStyle}>
        {header.map((item: ItemHeader, i: number) => {
          return (
            <View
              style={[
                item?.attr === "id" || item?.action === "delete" ? styles.firstIndexCellStyles : styles.cellStyles,
                { alignItems: "center" },
              ]}
              key={i}>
              <Paragraph numberOfLines={10} style={[styles.titleContentStyle, labelHeaderStyle]}>
                {item.name}
              </Paragraph>
            </View>
          );
        })}
      </DataTable.Header>

      {state.dataTable.slice(state.perPage * state.page, state.perPage * (state.page + 1)).map((item: any, i: number) => {
        return (
          <DataTable.Row onPress={() => onPressRowTable(item)} key={i} style={i % 2 !== 0 ? styles.evenRowStyles : styles.rowStyles}>
            {header.map((headerItem: ItemHeader, j: any) => {
              return (
                <View
                  style={[
                    headerItem?.attr === "id" || headerItem?.action === "delete" ? styles.firstIndexCellStyles : styles.cellStyles,
                    { alignItems: headerItem.notCenter ? "flex-start" : "center" },
                  ]}
                  key={j}>
                  {headerItem?.action?.length ? (
                    headerItem?.action === "delete" ? (
                      <IconButton
                        icon="delete"
                        size={spacing.huge}
                        color={colors.accent}
                        onPress={() => onPressChildren(item, headerItem.action)}
                      />
                    ) : headerItem?.action === "show" ? (
                      <Paragraph
                        numberOfLines={10}
                        onPress={() => onPressChildren(item, headerItem.action)}
                        style={[
                          styles.cellContent,
                          {
                            textAlign: "center",
                            color: colors.accent,
                            textDecorationLine: "underline",
                          },
                        ]}>
                        {t("COMMON_XEM")}
                      </Paragraph>
                    ) : null
                  ) : (
                    <Paragraph
                      numberOfLines={10}
                      style={[
                        styles.cellContent,
                        getDataTextStyle(item, headerItem, colors),
                        {
                          textAlign: headerItem.notCenter ? "left" : "center",
                        },
                      ]}>
                      {getValue(item, headerItem)}
                    </Paragraph>
                  )}
                </View>
              );
            })}
          </DataTable.Row>
        );
      })}
    </>
  );

  return (
    <>
      <DataTable>
        {widthCell && state.dataTable.length > 0 && (
          <ScrollView horizontal>
            <Block flex={1}>{tableView}</Block>
          </ScrollView>
        )}

        {!widthCell && state.dataTable.length > 0 && tableView}
        {state.dataTable.length > 0 && isPaginationView && (
          <DataTable.Pagination
            page={state.page}
            numberOfPages={state.numberOfPages}
            numberOfItemsPerPageList={numberOfItemsPerPageList}
            numberOfItemsPerPage={state.perPage}
            onItemsPerPageChange={(_numberOfItemsPerPage: number) => {
              setState({
                ...state,
                page: 0,
                perPage: _numberOfItemsPerPage,
                numberOfPages: Math.ceil(dataTable.length / _numberOfItemsPerPage),
              });
            }}
            selectPageDropdownLabel={t("COMMON_SO_DONG_TRANG")}
            onPageChange={(_page: number) => {
              setState({ ...state, page: _page });
            }}
            showFastPaginationControls
            label={t("COMMON_TRANG") + " " + (state.page + 1) + " / " + state.numberOfPages}
          />
        )}
      </DataTable>
    </>
  );
};

export default DataTableView;
